import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check } from '@opendate-app/setlist';
import '../../styles/beautifui.css';

const matchesQuery = (row, query) => !query
  || row.name.toLowerCase().includes(query)
  || (row.desc || '').toLowerCase().includes(query);

const ConductorPromptMenu = ({ sections, query = '', hint, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [engaged, setEngaged] = useState(false);
  const [glide, setGlide] = useState(null);
  const [maxHeight, setMaxHeight] = useState(null);
  const menuRef = useRef(null);
  const rowRefs = useRef([]);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = sections
    .map((section) => ({ ...section, rows: section.rows.filter((row) => matchesQuery(row, normalizedQuery)) }))
    .filter((section) => section.rows.length > 0);
  const rows = visibleSections.flatMap((section) => section.rows);

  useEffect(() => {
    setActiveIndex(0);
    setEngaged(false);
  }, [normalizedQuery]);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const ancestors = [];
    for (let element = menu.parentElement; element; element = element.parentElement) {
      ancestors.push(element);
    }
    const measure = () => {
      let clipTop = 0;
      ancestors.forEach((element) => {
        const style = window.getComputedStyle(element);
        if (/(hidden|auto|scroll|clip)/.test(`${style.overflow} ${style.overflowY}`)) {
          clipTop = Math.max(clipTop, element.getBoundingClientRect().top);
        }
      });
      setMaxHeight(Math.max(menu.getBoundingClientRect().bottom - clipTop - 8, 0));
    };
    measure();
    const observer = new ResizeObserver(measure);
    [menu, ...ancestors].forEach((element) => observer.observe(element));
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  useLayoutEffect(() => {
    const target = rowRefs.current[activeIndex];
    if (!target) return;
    setGlide({ top: target.offsetTop, height: target.offsetHeight });
    if (engaged) target.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, rows.length, engaged]);

  const choose = (row) => {
    row.onSelect();
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (rows.length === 0) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setEngaged(true);
        setActiveIndex((current) => (current + (event.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length);
      } else if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        choose(rows[activeIndex]);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  });

  let rowIndex = -1;
  return (
    <div
      ref={menuRef}
      className="bui bui-menu"
      role="listbox"
      aria-label="Add to request"
      style={maxHeight !== null ? { maxHeight } : undefined}
      onMouseLeave={() => setEngaged(false)}
    >
      <span
        aria-hidden="true"
        className="bui-menu-glide"
        style={{ top: glide?.top || 0, height: glide?.height || 0, opacity: glide && engaged && rows.length > 0 ? 1 : 0 }}
      />
      {visibleSections.map((section) => (
        <div key={section.key}>
          {section.title && <div className="bui-menu-section">{section.title}</div>}
          {section.rows.map((row) => {
            rowIndex += 1;
            const index = rowIndex;
            const Icon = row.icon;
            return (
              <button
                key={row.key}
                type="button"
                role="option"
                aria-selected={engaged && activeIndex === index}
                ref={(element) => { rowRefs.current[index] = element; }}
                className="bui-menu-row"
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => {
                  setActiveIndex(index);
                  setEngaged(true);
                }}
                onClick={() => choose(row)}
              >
                <span className="bui-menu-row-icon" aria-hidden="true">
                  {row.image ? <img src={row.image} alt="" /> : Icon && <Icon width={16} height={16} aria-hidden="true" />}
                </span>
                <span className="bui-menu-row-name">{row.name}</span>
                {row.desc && <span className="bui-menu-row-desc">{row.desc}</span>}
                {row.checked && <Check className="bui-menu-row-check" width={16} height={16} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      ))}
      {rows.length === 0 && <div className="bui-menu-empty">No matches for “{query}”</div>}
      {hint && <div className="bui-menu-hint">{hint}</div>}
    </div>
  );
};

ConductorPromptMenu.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string,
    rows: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      desc: PropTypes.string,
      icon: PropTypes.elementType,
      image: PropTypes.string,
      checked: PropTypes.bool,
      onSelect: PropTypes.func.isRequired
    })).isRequired
  })).isRequired,
  query: PropTypes.string,
  hint: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export default ConductorPromptMenu;
